/**
 * The single color scheme shared by every dropdown in this package — `BasicDropdown`,
 * `SearchableDropdown` and `BasicMultiselectDropdown`.
 *
 * It replaces the four hardcoded `*DropdownStyle` variants (`default`, `light`,
 * `dropdown_on_white_bg`, `bg_white`) that each of those three components used to carry its own
 * copy of. Those variants existed because the palette was baked in: a dropdown on a dark app page
 * needed different literals than one inside a white modal, and neither set worked on a
 * cinema-website domain themed by its operator. Two CSS variables express all three contexts, so
 * the choice a caller actually has to make collapses from "which palette" to "what is behind the
 * trigger" — see `DropdownTriggerVariant`.
 *
 * - `--non-transparent-surface-color` paints the options panel and every option.
 * - `--surface-tint-color` is the one tint on top of it: hover, selection, the panel hairline, and
 *   the trigger fill in the `tint` variant. Operator apps define it as a neutral grey, themed
 *   domains as a translucent primary; both are set in `getPageColorVariables`.
 *
 * Callers that need to break out of the scheme entirely (matching a live-preview canvas, say) pass
 * `classNameOverrides` instead of a variant.
 */

/**
 * What sits behind the trigger — which is the only thing a caller cannot derive from the theme.
 *
 * Each variant is named after the surface variable it fills itself with, so there is nothing to
 * remember: the pair it uses is `--<variant>-color` / `--on-<variant>-color`.
 *
 * - `tint` (default): the trigger sits ON a surface — a modal, a card — and fills itself with
 *   `--surface-tint-color` so it separates from that surface.
 * - `surface`: the translucent surface, `--surface-color`. For a trigger on the page background of a
 *   dark or image-backed page, where a wash of the surface color is the elevation and any opaque
 *   fill reads as a card that does not belong there. The footers' language pickers and the web
 *   home page's sorting dropdowns.
 * - `non-transparent-surface`: the opaque `--non-transparent-surface-color`, the same color the
 *   options panel is painted in. For a trigger that has to stay readable over arbitrary content,
 *   such as one inside a white modal whose fill would otherwise disappear into it.
 *
 * `surface` used to mean the opaque one, which was the confusing part: a variant called `surface`
 * taking a variable called `--non-transparent-surface-color` while `--surface-color` went unused.
 */
export type DropdownTriggerVariant = 'tint' | 'surface' | 'non-transparent-surface';

/**
 * The ink a trigger writes in — the variant's own `--on-*` partner.
 *
 * Separate from `dropdownTriggerClassName` because the combobox-style dropdowns have things to paint
 * that are NOT the input: the chevron sits beside it, so it inherits from the page rather than from
 * the trigger unless it is told. Left to inherit, those parts stayed the non-transparent surface's
 * dark ink while the trigger itself went white on the translucent variant.
 */
export function dropdownTriggerTextClassName(variant: DropdownTriggerVariant = 'tint'): string {
    return variant === 'surface' ? 'text-[var(--on-surface-color)]' : 'text-[var(--on-non-transparent-surface-color)]';
}

/**
 * Half-strength ink for a combobox input's placeholder, in the variant's own color. A combobox shows its
 * chosen option through the placeholder, so `isSelection` returns it in full ink: grey reads as "nothing chosen".
 */
export function dropdownTriggerPlaceholderClassName(
    variant: DropdownTriggerVariant = 'tint',
    isSelection = false
): string {
    if (variant === 'surface') {
        return isSelection
            ? 'placeholder-[rgba(var(--on-surface-color-rgb),1)]'
            : 'placeholder-[rgba(var(--on-surface-color-rgb),0.5)]';
    }
    return isSelection
        ? 'placeholder-[rgba(var(--on-non-transparent-surface-color-rgb),1)]'
        : 'placeholder-[rgba(var(--on-non-transparent-surface-color-rgb),0.5)]';
}

/** Trigger button: fill and ink per variant, all from the theme. */
export function dropdownTriggerClassName(variant: DropdownTriggerVariant = 'tint'): string {
    switch (variant) {
        case 'surface':
            // No hairline: the other two sit on a surface they can match exactly, so they need an
            // edge to be found at all. A translucent fill is always a shade of whatever it lies on,
            // so it already separates — while a `--surface-tint-color` hairline, a light grey in the
            // operator palettes, would draw a bright box around it on a dark page. `border` with no
            // color keeps the box metrics identical across the three.
            return [
                'border border-transparent',
                dropdownTriggerTextClassName(variant),
                'bg-[var(--surface-color)] hover:opacity-80'
            ].join(' ');
        case 'non-transparent-surface':
            return [
                'border border-[var(--surface-tint-color)]',
                'text-[var(--on-non-transparent-surface-color)]',
                'bg-[var(--non-transparent-surface-color)] hover:bg-[var(--surface-tint-color)]'
            ].join(' ');
        default:
            return [
                'border border-[var(--surface-tint-color)]',
                'text-[var(--on-non-transparent-surface-color)]',
                'bg-[var(--surface-tint-color)]'
            ].join(' ');
    }
}

/** Options panel: the surface itself, plus the hairline that separates it from what it overlaps. */
export const DROPDOWN_PANEL_CLASSNAME =
    'bg-[var(--non-transparent-surface-color)] text-[var(--on-non-transparent-surface-color)] border border-[var(--surface-tint-color)]';

/**
 * A single option. `data-focus` covers pointer hover and keyboard navigation alike — Headless UI
 * sets it for both — so there is no separate `hover:` rule to keep in sync. Its inset currentColor
 * outline is what tells the focused row apart from the selected one, which shares the same fill.
 */
export const DROPDOWN_OPTION_CLASSNAME = [
    'bg-[var(--non-transparent-surface-color)] data-[focus]:bg-[var(--surface-tint-color)] data-[selected]:bg-[var(--surface-tint-color)]',
    'data-[focus]:[outline:2px_solid_currentColor] data-[focus]:[outline-offset:-2px]'
].join(' ');

/**
 * A row in a menu-style panel whose items are plain links or buttons rather than Headless UI
 * options — the navigation menus, the booking burger menu, the shop links.
 *
 * `hover:` rather than `data-focus:`, because there is no Headless UI option state to read here.
 * No background of its own: every panel that uses this already paints itself (see `DropdownPanel`),
 * so a row only needs to say what it does when hovered.
 */
export const DROPDOWN_MENU_ITEM_CLASSNAME =
    'text-[var(--on-non-transparent-surface-color)] hover:bg-[var(--surface-tint-color)] transition-colors';

/**
 * Separator between rows or groups inside a panel, for use with Tailwind's `divide-y`.
 *
 * The tint doubles as the divider color so a panel needs exactly one token for its edge and its
 * internal rules. Worth knowing that on a themed domain the tint is a *translucent* primary, which
 * is why it also reads correctly over a ground other than the panel's own — the program filter's
 * mobile sheet relies on that.
 */
export const DROPDOWN_DIVIDER_CLASSNAME = 'divide-[var(--surface-tint-color)]';
